import type { ProgressState } from "@/types";

/** Draft matter lifecycle status */
export type DraftMatterStatus =
  | "setup"
  | "background"
  | "drafting"
  | "review"
  | "export"
  | "completed";

export type DraftSourceDocType =
  | "guideline"
  | "playbook"
  | "precedent"
  | "background"
  | "party-source";

export type PartyConfirmationState = "proposed" | "confirmed" | "edited";

export type SectionOrigin =
  | "template"
  | "approved-clause"
  | "user-fact"
  | "extracted-fact"
  | "ai-generated"
  | "user-edit";

export type ClausePosture =
  | "pro-company"
  | "balanced"
  | "pro-counterparty"
  | "market-standard";

export interface DraftTemplate {
  id: string;
  name: string;
  value: string;
  blurb: string | null;
  version: string;
  structure: unknown;
  template_file_path: string | null;
  is_system: boolean;
  user_id: string | null;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftLegalRole {
  id: string;
  name: string;
  role_key: string;
  instructions: string;
  is_system: boolean;
  user_id: string | null;
  org_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftMatter {
  id: string;
  matter_number: string;
  user_id: string;
  org_id: string;
  template_id: string | null;
  contract_type: string | null;
  legal_role_id: string | null;
  jurisdiction: string | null;
  title: string | null;
  background_info: string | null;
  agent_instructions: string | null;
  status: DraftMatterStatus | string;
  current_version_id: string | null;
  created_at: string;
  updated_at: string;
  is_owner?: boolean;
}

export interface DraftSourceDocument {
  id: string;
  matter_id: string;
  doc_type: DraftSourceDocType | string;
  file_path: string | null;
  file_name: string;
  extracted_text: string | null;
  extraction_status: string | null;
  created_at: string;
}

export interface DraftMatterDetail extends DraftMatter {
  legal_role_name?: string | null;
  parties: DraftParty[];
  conflicts: DraftFactConflict[];
  versions: DraftVersion[];
  source_documents?: DraftSourceDocument[];
}

export interface DraftVersion {
  id: string;
  matter_id: string;
  version_number: number;
  content_json: DraftDocumentModel | unknown;
  content_html: string | null;
  section_provenance: SectionProvenance[] | unknown;
  is_approved: boolean;
  created_by: string;
  created_at: string;
}

export interface DraftParty {
  id: string;
  matter_id: string;
  label: string;
  legal_name: string | null;
  entity_type: string | null;
  registration_data: Record<string, unknown> | null;
  address_data: Record<string, unknown> | null;
  signatory_data: Record<string, unknown> | null;
  source_ref: string | null;
  confirmation_state: PartyConfirmationState;
  created_at: string;
  updated_at: string;
}

export interface DraftFactConflict {
  id: string;
  matter_id: string;
  field: string;
  candidates: Array<{ value: string; source: string }>;
  resolved_value: string | null;
  status: "open" | "resolved" | string;
  created_at: string;
  updated_at: string;
}

export interface DraftSourceDocumentInput {
  id: string;
  docType: DraftSourceDocType;
  fileName: string;
  /** Present for new local picks; omitted after persisted to the server. */
  file?: File;
  /** Saved on the matter (reloads after refresh). */
  persisted?: boolean;
}

export interface DraftClauseAlternative {
  id: string;
  template_id: string;
  section_key: string;
  clause_category: string | null;
  language: string;
  posture: ClausePosture | string | null;
  jurisdiction_applicability: string | null;
  source_document_id: string | null;
  approval_status: string | null;
  version: string | null;
  last_reviewed_at: string | null;
  is_system?: boolean;
  org_id?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface DraftTemplateSectionOption {
  key: string;
  title: string;
}

export interface DraftReviewFinding {
  id: string;
  matter_id: string;
  version_id: string | null;
  severity: string;
  rule: string | null;
  section_id: string | null;
  title: string;
  explanation: string;
  proposed_revision: string | null;
  status: string;
  resolution_note: string | null;
  reviewer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftReviewMetrics {
  playbook_conformance_percent: number;
  active_issues: number;
  resolved_items: number;
}

export interface DraftReviewGenerateResponse {
  findings: DraftReviewFinding[];
  metrics: DraftReviewMetrics;
  grounding_summary: string;
}

export interface SectionProvenance {
  sectionId: string;
  origin: SectionOrigin;
  sourceDocumentId?: string;
  sourceVersion?: string;
  clauseCategory?: string;
  jurisdictionApplicability?: string;
  posture?: ClausePosture;
  approvalStatus?: "approved" | "unverified";
  lastReviewedAt?: string;
  clauseAlternativeId?: string;
}

export interface ProseMirrorNode {
  type: string;
  attrs?: Record<string, unknown>;
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  content?: ProseMirrorNode[];
}

export interface ProseMirrorDoc {
  type: "doc";
  content?: ProseMirrorNode[];
}

export interface DraftSection {
  sectionId: string;
  sectionKey: string;
  title: string;
  content: ProseMirrorDoc;
}

export interface DraftDocumentModel {
  type: "draft-document";
  title: string;
  sections: DraftSection[];
  meta?: {
    matterNumber?: string;
    jurisdiction?: string;
    templateId?: string;
    templateVersion?: string;
  };
}

export interface DraftCopilotProposal {
  explanation: string;
  target_section_key: string;
  proposed_content?: ProseMirrorDoc | unknown;
  provenance?: SectionProvenance | unknown;
}

export interface DraftCopilotResponse {
  mode: string;
  applied: boolean;
  version_id?: string;
  content_json?: DraftDocumentModel;
  proposal: DraftCopilotProposal;
}

export interface DraftCopilotHistoryMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  metadata?: {
    source_instruction?: string;
    copilot?: {
      mode: string;
      applied: boolean;
      explanation?: string;
      target_section_key?: string;
      version_id?: string;
    };
  } | null;
}

export interface DraftExportResult {
  file_path: string;
  download_url: string;
  file_name: string;
  sha256?: string;
  content_type: string;
}

export interface CreateDraftMatterInput {
  template_id?: string;
  contract_type?: string;
  legal_role_id?: string;
  jurisdiction?: string;
  title?: string;
  background_info?: string;
  agent_instructions?: string;
  status?: DraftMatterStatus;
}

export interface UpdateDraftMatterInput extends Partial<CreateDraftMatterInput> {
  content_json?: unknown;
  content_html?: string;
}

export interface DraftWizardData {
  /** `draft_templates.id` — drafting catalog, not contract-review contract types. */
  templateId: string | null;
  /** Template `value` stored on the matter as `contract_type` for generation. */
  contractType: string;
  legalRoleId: string | null;
  jurisdiction: string;
  title: string;
  backgroundInfo: string;
  agentInstructions: string;
  sourceDocuments: DraftSourceDocumentInput[];
  parties: DraftPartyInput[];
}

export interface DraftPartyInput {
  label: string;
  legal_name?: string | null;
  entity_type?: string | null;
  registration_data?: Record<string, unknown> | null;
  address_data?: Record<string, unknown> | null;
  signatory_data?: Record<string, unknown> | null;
  source_ref?: string | null;
  confirmation_state?: PartyConfirmationState;
}

export interface DraftStreamCompletion {
  matterId?: string;
  versionId?: string;
  status?: string;
  result?: { documentModel?: DraftDocumentModel };
}

export interface DraftGenerationState {
  progress: ProgressState;
  isGenerating: boolean;
  error: string | null;
}

export const DRAFT_SOURCE_DOC_LABELS: Record<DraftSourceDocType, string> = {
  guideline: "GUIDELINE",
  playbook: "PLAYBOOK",
  precedent: "PRECEDENT",
  background: "BACKGROUND",
  "party-source": "PARTY SOURCE",
};

export const DRAFT_WIZARD_STEPS = [
  { id: 1, key: "setup", title: "Setup", description: "Contract type, role, jurisdiction" },
  { id: 2, key: "background", title: "Background", description: "Context and parties" },
  { id: 3, key: "draft", title: "Draft", description: "Edit, variants, and Copilot" },
  { id: 4, key: "review", title: "Review", description: "Findings and actions" },
  { id: 5, key: "export", title: "Export", description: "Download documents" },
] as const;
